export type Preference = "unknown" | "granted" | "denied";
export type MarketingProvider = "meta" | "google";
export function isMarketingProvider(
  value: unknown,
): value is MarketingProvider {
  return value === "meta" || value === "google";
}
export interface MarketingCommerceItem {
  ticket_id: number;
  quantity: number;
  unit_price_cents: number;
  name?: string;
  category?: string;
  variant?: string;
}
export interface MarketingJourneyContext {
  event_name?: string;
  event_category?: string;
  currency?: string;
  value_cents?: number;
  items?: MarketingCommerceItem[];
}
export interface MarketingMeasurementSession {
  measurement_receipt: string;
  policy_version: "platform_measurement_v1";
  analytics_enabled: boolean;
  advertising_enabled: boolean;
}
export interface MarketingConsent {
  consent_receipt: string;
  analytics_state: Preference;
  advertising_state: Preference;
  policy_version?: string;
}
export interface MarketingDestination {
  provider: MarketingProvider;
  public_destination_id?: string;
  public_configuration: Record<string, string>;
  allowed_events?: string[];
  browser_enabled?: boolean;
  provider_event?: string;
  deduplication_id?: string;
}
export interface ConversionDescriptor {
  order_id: number;
  browser_eligible_until?: string;
  reason?: string;
  conversions: {
    conversion_id: string;
    event_id: number;
    type: string;
    currency: string;
    ads_value_cents: number;
    commercial_units: number;
    items?: MarketingCommerceItem[];
    event_name?: string;
    event_category?: string;
    destinations: MarketingDestination[];
  }[];
}
export interface MarketingReport {
  filter_options?: Record<"source" | "medium" | "campaign", string[]>;
  order_rows?: {
    order_id: number;
    occurred_at: string;
    source: string;
    campaign: string;
    ticket_revenue_cents: number;
    first_touch?: Record<string, string> | null;
    last_touch?: Record<string, string> | null;
  }[];
  order_pagination?: { page: number; per_page: number; total: number };
  funnel?: {
    available: boolean;
    reason?: string;
    sessions?: {
      event_viewed: number;
      cart_item_added: number;
      checkout_started: number;
      purchase_confirmed: number;
    };
    conversion_percent?: number | null;
    metadata: {
      cohort: string;
      as_of: string;
      window_seconds: number;
      unmatched_stage_events?: number;
    };
  };
  summary: {
    paid_orders: number;
    commercial_units: number;
    ticket_revenue_cents: number;
    refund_cents: number;
    revenue_after_refunds_cents: number;
    identified_orders: number;
    online_orders: number;
    coverage_percent: number;
  };
  rows: {
    source: string;
    medium: string;
    campaign: string;
    sale_channel: string;
    orders: number;
    commercial_units: number;
    ticket_revenue_cents: number;
    refund_cents: number;
    revenue_after_refunds_cents: number;
  }[];
  metadata: {
    generated_at: string;
    timezone: string;
    model: string;
    monetary_policy_version: string;
    unattributed_reasons?: Record<string, number>;
    historical_excluded?: boolean;
    recording_gaps?: Record<string, number>;
    refund_reviews?: number;
  };
  pagination?: { page: number; per_page: number; total: number };
}
export interface MarketingIntegration {
  event_id?: number | null;
  id: number;
  provider: MarketingProvider;
  display_name: string;
  public_configuration: Record<string, string>;
  enabled: boolean;
  capability_status: string;
  browser_available?: boolean;
  lock_version: number;
}
export interface MarketingBinding {
  id?: number;
  integration_id: number;
  provider?: MarketingProvider;
  enabled: boolean;
  browser_enabled: boolean;
  allowed_events: string[];
  configuration_version?: number;
}
